/**
 * DAS2 List Controller (EPIC-023 / 23.5 — §12).
 *
 * Page : `/das2.html`. Vue principale des bénéficiaires DAS2 d'un exercice :
 *   - chargement de l'entête (GET …/declarations/{year}) — stats agrégées
 *     et identité du fournisseur
 *   - chargement détaillé des bénéficiaires (GET …/beneficiaries) avec
 *     rendu via <template data-slot> + cloneTemplate + fillSlots
 *   - rafraîchissement déterministe (POST …/refresh, upsert das2_beneficiary)
 *   - filtres client (statut, catégorie, recherche texte) — pure
 *     manipulation DOM, aucun appel API supplémentaire
 *   - délégation de la complétion à `das2-beneficiary-form` via
 *     l'événement DOM `das2:openBeneficiaryForm`
 *
 * Le seuil DAS2 (2 400 € TTC/an/bénéficiaire) et la liste des catégories
 * éligibles sont décidés côté serveur. Le controller ne fait aucun calcul
 * d'éligibilité.
 */

import BaseController from "./base_controller.js";
import { api, APIError } from "../services/api.js";

const STATUS_LABELS = {
  pending: "à valider",
  validated: "validé",
  excluded: "exclu",
};

const STATUS_PILL_CLASSES = {
  pending: "nm-pill nm-pill--ochre",
  validated: "nm-pill nm-pill--olive",
  excluded: "nm-pill",
};

const STATUS_FILTER_VALUES = {
  "Statut : tous": null,
  "À valider": "pending",
  "Validé": "validated",
  "Exclu": "excluded",
};

const CATEGORY_LABELS = {
  honoraires_pro_lib: "Honoraires prof. lib.",
  sous_traitance_indep: "Sous-traitance indép.",
  commission_apporteur: "Commission apporteur",
  cession_droits_auteur: "Cession droits d'auteur",
};

const CATEGORY_FILTER_VALUES = {
  "Catégorie : toutes": null,
  "Honoraires (prof. lib.)": "honoraires_pro_lib",
  "Sous-traitance indép.": "sous_traitance_indep",
  "Commission apporteur": "commission_apporteur",
  "Cession droits d'auteur": "cession_droits_auteur",
};

export default class Das2ListController extends BaseController {
  static targets = [
    "crumbYear",
    "overline",
    "title",
    "subtitle",
    "prevYearButton",
    "prepareLink",
    "statDetected",
    "statDetectedSub",
    "statPending",
    "statPendingSub",
    "statValidated",
    "statValidatedSub",
    "statTotalTtc",
    "statTotalTtcSub",
    "filterStatus",
    "filterCategory",
    "filterSearch",
    "tbody",
    "emptyState",
    "footerCount",
    "refreshButton",
  ];

  static values = {
    year: { type: Number, default: 0 },
  };

  connect() {
    if (!this.yearValue) {
      this.yearValue = new Date().getFullYear() - 1;
    }
    this._beneficiaries = [];
    this._currentYear = this.yearValue;
    // Un seul refresh automatique par session de page : au 1ᵉʳ accès à un
    // exercice sans brouillon (404), la page déclenche la détection puis
    // recharge. Ne se rejoue pas sur « Exercice N-1 » (cf. 99.77).
    this._autoRefreshed = false;
    this._beneficiaryUpdatedHandler = () => this._onBeneficiaryUpdated();
    window.addEventListener("das2:beneficiaryUpdated", this._beneficiaryUpdatedHandler);
    this._renderYear(this._currentYear);
    this.loadDeclaration();
  }

  disconnect() {
    if (this._beneficiaryUpdatedHandler) {
      window.removeEventListener("das2:beneficiaryUpdated", this._beneficiaryUpdatedHandler);
    }
  }

  _onBeneficiaryUpdated() {
    this.loadBeneficiaries();
    this.loadDeclaration();
  }

  async loadDeclaration() {
    try {
      const data = await api.das2.getDeclaration(this._currentYear);
      this._renderHead(data);
      await this.loadBeneficiaries();
    } catch (error) {
      if (error instanceof APIError && error.status === 404) {
        // 1ᵉʳ accès : aucun brouillon `das2_declaration` n'existe tant que la
        // détection n'a pas tourné. On la déclenche automatiquement (idempotent)
        // puis on recharge une seule fois (drapeau anti-boucle).
        if (!this._autoRefreshed) {
          this._autoRefreshed = true;
          try {
            await api.das2.refresh(this._currentYear);
            await this.loadDeclaration();
            return;
          } catch {
            // Refresh KO (org sans aucune facture candidate, ou erreur) :
            // on bascule en empty state propre, sans boucler.
          }
        }
        this._renderEmpty();
        return;
      }
      this.showError(this._messageForError(error, "Impossible de charger la déclaration DAS2."));
    }
  }

  async loadBeneficiaries() {
    try {
      const data = await api.das2.listBeneficiaries(this._currentYear);
      const list = Array.isArray(data) ? data : (data?.data ?? []);
      this._beneficiaries = list;
      this._renderRows(list);
      this.applyFilters();
    } catch (error) {
      this.showError(this._messageForError(error, "Impossible de charger les bénéficiaires."));
    }
  }

  async refresh() {
    if (this.hasRefreshButtonTarget) {
      this.refreshButtonTarget.disabled = true;
      this.refreshButtonTarget.textContent = "Rafraîchissement…";
    }
    try {
      await api.das2.refresh(this._currentYear);
      await this.loadDeclaration();
      this.showSuccess("Détection DAS2 rafraîchie.");
    } catch (error) {
      this.showError(this._messageForError(error, "Échec du rafraîchissement de la détection."));
    } finally {
      if (this.hasRefreshButtonTarget) {
        this.refreshButtonTarget.disabled = false;
        this.refreshButtonTarget.textContent = "Rafraîchir la détection";
      }
    }
  }

  previousYear() {
    this._currentYear = this._currentYear - 1;
    this._renderYear(this._currentYear);
    this.loadDeclaration();
  }

  applyFilters() {
    if (!this.hasTbodyTarget) return;

    const statusFilter = this.hasFilterStatusTarget
      ? STATUS_FILTER_VALUES[this.filterStatusTarget.value] ?? null
      : null;
    const categoryFilter = this.hasFilterCategoryTarget
      ? CATEGORY_FILTER_VALUES[this.filterCategoryTarget.value] ?? null
      : null;
    const searchRaw = this.hasFilterSearchTarget ? this.filterSearchTarget.value.trim().toLowerCase() : "";

    const rows = this.tbodyTarget.querySelectorAll("tr[data-beneficiary-id]");
    let visibleCount = 0;
    rows.forEach((row) => {
      const status = row.getAttribute("data-status");
      const category = row.getAttribute("data-category");
      const searchText = row.getAttribute("data-search-text") || "";
      const matchesStatus = statusFilter === null || status === statusFilter;
      const matchesCategory = categoryFilter === null || category === categoryFilter;
      const matchesSearch = searchRaw === "" || searchText.includes(searchRaw);
      const visible = matchesStatus && matchesCategory && matchesSearch;
      row.hidden = !visible;
      if (visible) visibleCount += 1;
    });

    if (this.hasEmptyStateTarget) {
      this.emptyStateTarget.hidden = visibleCount > 0;
    }
    if (this.hasFooterCountTarget) {
      this.footerCountTarget.textContent = String(visibleCount);
    }
  }

  actionClick(event) {
    event.preventDefault();
    const link = event.currentTarget;
    const row = link.closest("tr[data-beneficiary-id]");
    if (!row) return;
    const beneficiaryId = row.getAttribute("data-beneficiary-id");
    const intent = link.getAttribute("data-intent");

    if (intent === "complete" || intent === "detail") {
      this._dispatchOpenForm(beneficiaryId);
      return;
    }
    if (intent === "reinclude") {
      this._patchBeneficiary(beneficiaryId, "reinclude");
    }
  }

  _dispatchOpenForm(beneficiaryId) {
    const beneficiary = this._beneficiaries.find((b) => b.id === beneficiaryId);
    if (!beneficiary) return;
    window.dispatchEvent(new CustomEvent("das2:openBeneficiaryForm", { detail: { beneficiary } }));
  }

  async _patchBeneficiary(beneficiaryId, action) {
    try {
      await api.das2.updateBeneficiary(beneficiaryId, action);
      await this.loadBeneficiaries();
      await this.loadDeclaration();
    } catch (error) {
      this.showError(this._messageForError(error, "Impossible de mettre à jour le bénéficiaire."));
    }
  }

  _renderYear(year) {
    if (this.hasCrumbYearTarget) this.crumbYearTarget.textContent = String(year);
    if (this.hasOverlineTarget) {
      this.overlineTarget.textContent = `Honoraires versés à des tiers — exercice ${year}`;
    }
    if (this.hasPrevYearButtonTarget) {
      this.prevYearButtonTarget.textContent = `← Exercice ${year - 1}`;
    }
    if (this.hasPrepareLinkTarget) {
      this.prepareLinkTarget.href = `/das2-declaration.html?year=${year}`;
    }
  }

  _renderHead(data) {
    const year = data?.fiscalYear ?? this._currentYear;
    this._currentYear = year;
    this._renderYear(year);

    const totals = data?.totals ?? {};
    const detected = Number(totals.detectedCount ?? 0);
    const pending = Number(totals.pendingCount ?? 0);
    const validated = Number(totals.validatedCount ?? 0);
    const totalSuppliers = Number(totals.totalSuppliersCount ?? 0);
    const totalTtc = Number(totals.totalTtc ?? 0);
    const deadline = data?.deadlineDate ?? null;

    if (this.hasTitleTarget) this.titleTarget.textContent = "DAS2 — bénéficiaires concernés";
    if (this.hasSubtitleTarget) {
      const deadlineText = deadline ? this.formatShortDate(deadline) : `5 mai ${year + 1}`;
      this.subtitleTarget.replaceChildren();
      this.subtitleTarget.append(
        document.createTextNode(
          "Notamicro identifie automatiquement les tiers à déclarer (honoraires, sous-traitance indépendante, commissions, droits d'auteur ≥ ",
        ),
      );
      const seuil = document.createElement("span");
      seuil.className = "mono";
      seuil.textContent = "2 400 € TTC/an";
      this.subtitleTarget.append(seuil);
      this.subtitleTarget.append(document.createTextNode("). Validez ou excluez chaque ligne avant la déclaration du "));
      const strong = document.createElement("strong");
      strong.textContent = deadlineText;
      this.subtitleTarget.append(strong);
      this.subtitleTarget.append(document.createTextNode("."));
    }
    if (this.hasStatDetectedTarget) this.statDetectedTarget.textContent = String(detected);
    if (this.hasStatDetectedSubTarget) {
      this.statDetectedSubTarget.textContent = `Sur ${totalSuppliers} fournisseurs ${year}`;
    }
    if (this.hasStatPendingTarget) this.statPendingTarget.textContent = String(pending);
    if (this.hasStatPendingSubTarget) {
      this.statPendingSubTarget.textContent = pending > 0 ? "Données incomplètes" : "Tout est validé";
    }
    if (this.hasStatValidatedTarget) this.statValidatedTarget.textContent = String(validated);
    if (this.hasStatValidatedSubTarget) {
      this.statValidatedSubTarget.textContent = validated > 0 ? "Prêts pour la déclaration" : "Aucun bénéficiaire validé";
    }
    if (this.hasStatTotalTtcTarget) {
      this.statTotalTtcTarget.replaceChildren();
      this.statTotalTtcTarget.append(document.createTextNode(this._formatAmount(totalTtc, 0)));
      const small = document.createElement("small");
      small.textContent = " €";
      this.statTotalTtcTarget.append(small);
    }
    if (this.hasStatTotalTtcSubTarget) {
      this.statTotalTtcSubTarget.textContent = "Sur les lignes validées";
    }
  }

  _renderRows(list) {
    if (!this.hasTbodyTarget) return;
    this.tbodyTarget.replaceChildren();

    list.forEach((b) => {
      const fragment = this.cloneTemplate("das2-beneficiary-row-template");
      const row = fragment.querySelector("tr");
      if (!row) return;
      row.setAttribute("data-beneficiary-id", b.id ?? "");
      row.setAttribute("data-status", b.status ?? "pending");
      row.setAttribute("data-category", b.category ?? "");
      const searchText = [b.supplierName ?? "", b.siret ?? "", b.legalForm ?? ""]
        .join(" ")
        .toLowerCase();
      row.setAttribute("data-search-text", searchText);

      const siretCell = fragment.querySelector('[data-slot="siret"]');
      const hasSiret = typeof b.siret === "string" && b.siret.trim() !== "";
      this.fillSlots(fragment, {
        supplierName: b.supplierName ?? "—",
        invoicesSummary: this._invoicesSummary(b),
        category: CATEGORY_LABELS[b.category] ?? b.category ?? "—",
        legalForm: b.legalForm ?? "—",
        totalTtc: `${this._formatAmount(Number(b.computedTotalTtc ?? 0))} €`,
        statusLabel: STATUS_LABELS[b.status] ?? b.status ?? "—",
      });

      if (siretCell) {
        siretCell.replaceChildren();
        if (hasSiret) {
          siretCell.textContent = this._formatSiret(b.siret);
        } else {
          siretCell.append(document.createTextNode("— "));
          const pill = document.createElement("span");
          pill.className = "nm-pill nm-pill--ochre fs-9";
          pill.textContent = "manquant";
          siretCell.append(pill);
        }
      }

      const statusPill = fragment.querySelector('[data-slot="statusPill"]');
      if (statusPill) {
        statusPill.className = STATUS_PILL_CLASSES[b.status] ?? "nm-pill";
      }

      const actionLink = fragment.querySelector('[data-slot="actionLink"]');
      if (actionLink) {
        actionLink.style.textDecoration = "underline";
        const { label, intent } = this._actionFor(b);
        actionLink.textContent = label;
        actionLink.setAttribute("data-intent", intent);
      }

      this.tbodyTarget.append(fragment);
    });
  }

  _actionFor(beneficiary) {
    if (beneficiary.status === "excluded") {
      return { label: "Réinclure", intent: "reinclude" };
    }
    if (beneficiary.status === "validated") {
      return { label: "Détail", intent: "detail" };
    }
    return { label: "Compléter", intent: "complete" };
  }

  _invoicesSummary(beneficiary) {
    const count = Number(beneficiary.invoiceCount ?? (beneficiary.sourceInvoiceIds?.length ?? 0));
    if (count === 0) return "";
    if (count === 1) return "1 facture";
    return `${count} factures`;
  }

  _formatSiret(raw) {
    const digits = String(raw).replace(/\D/g, "");
    if (digits.length !== 14) return String(raw);
    return `${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6, 9)} ${digits.slice(9)}`;
  }

  _formatAmount(amount, decimals = 2) {
    return amount.toLocaleString("fr-FR", {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    });
  }

  _renderEmpty() {
    this._beneficiaries = [];
    // Réinitialise les 4 cartes de stats à 0 (via le rendu d'entête zéroé)
    // pour sortir du placeholder `nm-loading` figé sur un exercice vide.
    this._renderHead({ fiscalYear: this._currentYear, totals: {} });
    if (this.hasTbodyTarget) this.tbodyTarget.replaceChildren();
    if (this.hasEmptyStateTarget) this.emptyStateTarget.hidden = false;
    if (this.hasFooterCountTarget) this.footerCountTarget.textContent = "0";
  }

  _messageForError(error, fallback) {
    if (error instanceof APIError) {
      if (error.status === 403) return "Vous n'avez pas accès à cette déclaration.";
      if (error.status === 423) return "Cette déclaration est verrouillée (soumise).";
      return error.message || fallback;
    }
    return fallback;
  }

}
